import React from "react";
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { ConfirmDialogHost } from "@/components/ui/confirm-dialog";
import { OfflineOverlay } from "@/components/OfflineOverlay";
import { ToastProvider } from "@/contexts/ToastContext";
import { AuthProvider } from "@/features/auth/contexts/AuthContext";
import { ThemeProvider } from "@/features/theme/contexts/ThemeContext";
import { LanguageProvider } from "@/features/language/contexts/LanguageContext";
import { ProtectedRoute } from "@/features/auth/components/ProtectedRoute";
import { PermissionRoute } from "@/features/auth/components/PermissionRoute";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { ForgotPasswordPage } from "@/features/auth/pages/ForgotPasswordPage";
import { DashboardLayout } from "@/features/layout/components/DashboardLayout";
import DashboardPage from "@/features/dashboard/pages/DashboardPage";
import ManagementHubPage from "@/features/management/pages/ManagementHubPage";
import EmployeeDetailPage from "@/features/users/pages/UsersDetailPage";
import UserCreatePage from "@/features/users/pages/UserCreatePage";
import ProductsPage from "@/features/products/pages/ProductsPage";
import ProductDetailPage from "@/features/products/pages/ProductDetailPage";
import RecipeBuilderPage from "@/features/recipes/pages/RecipeBuilderPage";
import SalesHubPage from "@/features/sales/pages/SalesHubPage";
import AddSalesPage from "@/features/sales/pages/AddSalesPage";
import AddQuotationPage from "@/features/quotations/pages/AddQuotationPage";
import QuotationDetailPage from "@/features/quotations/pages/QuotationDetailPage";
import SalesDetailPage from "@/features/sales/pages/SalesDetailPage";
import AddPurchasePage from "@/features/purchases/pages/AddPurchasePage";
import PurchaseDetailPage from "@/features/purchases/pages/PurchaseDetailPage";
import ContactsPage from "@/features/contacts/pages/ContactsPage";
import ReportsPage from "@/features/reports/pages/ReportsPage";
import ReportViewerPage from "@/features/reports/pages/ReportViewerPage";
import IntegrationsPage from "@/features/settings/pages/IntegrationsPage";
import ExpensesPage from "@/features/expenses/pages/ExpensesPage";
import PaymentsHubPage from "@/features/payments/pages/PaymentsHubPage";
import CheckoutHubPage from "@/features/checkout/pages/CheckoutHubPage";
import CompanyPage from "@/features/company/pages/CompanyPage";
import AddReturnPage from "@/features/returns/pages/AddReturnPage";
import NotificationsPage from "@/features/notifications/pages/NotificationsPage";
import NotificationDetailPage from "@/features/notifications/pages/NotificationDetailPage";
import HelpPage from "@/features/help/pages/HelpPage";
import InvoiceDetailPage from "@/features/invoices/pages/InvoiceDetailPage";
import InvoiceEditPage from "@/features/invoices/pages/InvoiceEditPage";
import PosListPage from "@/features/pos/pages/PosListPage";
import PosDetailPage from "@/features/pos/pages/PosDetailPage";
import PosViewPage from "@/features/pos/pages/PosViewPage";
import ReturnDetailPage from "@/features/returns/pages/ReturnDetailPage";
import ReturnEditPage from "@/features/returns/pages/ReturnEditPage";
import StockTransferDetailPage from "@/features/stock-transfers/pages/StockTransferDetailPage";
import ProfilePage from "@/features/profile/pages/ProfilePage";
import AuditLogPage from "@/features/audit/pages/AuditLogPage";

/** Old expense detail/edit URLs land on /expenses with the edit dialog open. */
function ExpenseEditRedirect() {
	const { id } = useParams<{ id: string }>();
	return <Navigate to={`/expenses?edit=${id ?? ""}`} replace />;
}

/** Old customer/supplier edit URLs land on /contacts with the edit dialog open. */
function ContactEditRedirect({ type }: { type: "customers" | "suppliers" }) {
	const { id } = useParams<{ id: string }>();
	return <Navigate to={`/contacts?type=${type}&edit=${id ?? ""}`} replace />;
}

/** Old cross-branch search URLs land on /products with the checkbox on, keeping the query string. */
function CrossBranchRedirect() {
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	params.set("crossBranch", "1");
	return <Navigate to={{ pathname: "/products", search: `?${params.toString()}` }} replace />;
}

// Create a client
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 1,
			refetchOnWindowFocus: false,
		},
	},
});

/**
 * The whole route tree, rendered under a single catch-all data-router route. Using a data router
 * (createBrowserRouter) instead of BrowserRouter is what lets pages block navigation with
 * useBlocker when a form has unsaved changes.
 */
function AppRoutes() {
	return (
		<>
			<Routes>
				{/* Public Routes */}
				<Route path="/login" element={<LoginPage />} />
				<Route path="/forgot-password" element={<ForgotPasswordPage />} />

				{/* Protected Routes */}
				<Route
					path="/"
					element={
						<ProtectedRoute>
							<DashboardLayout />
						</ProtectedRoute>
					}
				>
					<Route
						index
						element={
							<PermissionRoute permission="dashboard.read">
								<DashboardPage />
							</PermissionRoute>
						}
					/>
					<Route path="profile" element={<ProfilePage />} />
					<Route
						path="management"
						element={
							<PermissionRoute permission={["users.read", "roles.read", "departments.read"]}>
								<ManagementHubPage />
							</PermissionRoute>
						}
					/>
					<Route path="users" element={<Navigate to="/management?type=users" replace />} />
					<Route
						path="users/new"
						element={
							<PermissionRoute permission="users.create">
								<UserCreatePage />
							</PermissionRoute>
						}
					/>
					<Route
						path="users/:id"
						element={
							<PermissionRoute permission="users.read">
								<EmployeeDetailPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="products"
						element={
							<PermissionRoute
								permission={[
									"inventory.read",
									"products.read",
									"products.readCrossBranch",
									"stockTransfers.read",
								]}
							>
								<ProductsPage />
							</PermissionRoute>
						}
					/>
					<Route path="products/cross-branch" element={<CrossBranchRedirect />} />
					<Route
						path="products/new"
						element={
							<PermissionRoute permission={["inventory.create", "products.create"]}>
								<ProductDetailPage />
							</PermissionRoute>
						}
					/>
					<Route path="product-cross-branch" element={<CrossBranchRedirect />} />
					<Route
						path="products/:id"
						element={
							<PermissionRoute permission={["inventory.read", "products.read"]}>
								<ProductDetailPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="recipes/product/:productId"
						element={
							<PermissionRoute permission={["recipes.read", "recipes.update"]}>
								<RecipeBuilderPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="sales"
						element={
							<PermissionRoute permission={["sales.read", "returns.read", "purchases.read"]}>
								<SalesHubPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="sales/new"
						element={
							<PermissionRoute permission="sales.create">
								<AddSalesPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="sales/:id"
						element={
							<PermissionRoute permission="sales.read">
								<SalesDetailPage />
							</PermissionRoute>
						}
					/>
					<Route path="quotations" element={<Navigate to="/sales?type=quotations" replace />} />
					<Route
						path="quotations/new"
						element={
							<PermissionRoute permission="sales.create">
								<AddQuotationPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="quotations/:id/edit"
						element={
							<PermissionRoute permission="sales.update">
								<AddQuotationPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="quotations/:id"
						element={
							<PermissionRoute permission="sales.read">
								<QuotationDetailPage />
							</PermissionRoute>
						}
					/>
					<Route path="purchases" element={<Navigate to="/sales?type=purchases" replace />} />
					<Route
						path="purchases/new"
						element={
							<PermissionRoute permission="purchases.create">
								<AddPurchasePage />
							</PermissionRoute>
						}
					/>
					<Route
						path="purchases/:id"
						element={
							<PermissionRoute permission="purchases.read">
								<PurchaseDetailPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="purchases/:id/edit"
						element={
							<PermissionRoute permission="purchases.update">
								<AddPurchasePage />
							</PermissionRoute>
						}
					/>
					<Route
						path="contacts"
						element={
							<PermissionRoute permission={["customers.read", "suppliers.read"]}>
								<ContactsPage />
							</PermissionRoute>
						}
					/>
					<Route path="customers" element={<Navigate to="/contacts" replace />} />
					<Route path="customers/new" element={<Navigate to="/contacts?new=1" replace />} />
					<Route path="customers/:id" element={<ContactEditRedirect type="customers" />} />
					<Route path="suppliers" element={<Navigate to="/contacts?type=suppliers" replace />} />
					<Route path="suppliers/new" element={<Navigate to="/contacts?type=suppliers&new=1" replace />} />
					<Route path="suppliers/:id" element={<ContactEditRedirect type="suppliers" />} />
					<Route
						path="reports"
						element={
							<PermissionRoute permission="reports.read">
								<ReportsPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="reports/:reportId"
						element={
							<PermissionRoute permission="reports.read">
								<ReportViewerPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="pos/list"
						element={
							<PermissionRoute permission="pos.read">
								<PosListPage />
							</PermissionRoute>
						}
					/>
					<Route path="pos/shifts" element={<Navigate to="/pos/list" replace />} />
					<Route
						path="pos/new"
						element={
							<PermissionRoute permission="pos.create">
								<PosDetailPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="pos/:id"
						element={
							<PermissionRoute permission="pos.read">
								<PosViewPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="pos/:id/edit"
						element={
							<PermissionRoute permission="pos.update">
								<PosDetailPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="audit"
						element={
							<PermissionRoute permission="audit.read">
								<AuditLogPage />
							</PermissionRoute>
						}
					/>
					<Route path="invoices" element={<Navigate to="/sales?type=invoices" replace />} />
					<Route
						path="invoices/:id"
						element={
							<PermissionRoute permission="sales.read">
								<InvoiceDetailPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="invoices/:id/edit"
						element={
							<PermissionRoute permission="sales.update">
								<InvoiceEditPage />
							</PermissionRoute>
						}
					/>
					<Route path="settings" element={<Navigate to="/settings/integrations" replace />} />
					<Route
						path="settings/integrations"
						element={
							<PermissionRoute permission="settings.read">
								<IntegrationsPage />
							</PermissionRoute>
						}
					/>
					<Route path="settings/system" element={<Navigate to="/company" replace />} />
					<Route path="settings/notifications" element={<Navigate to="/company" replace />} />
					<Route path="settings/addons" element={<Navigate to="/settings/integrations" replace />} />
					<Route path="settings/addon-requests" element={<Navigate to="/settings/integrations" replace />} />
					<Route
						path="expenses"
						element={
							<PermissionRoute permission="expenses.read">
								<ExpensesPage />
							</PermissionRoute>
						}
					/>
					<Route path="expense-categories" element={<Navigate to="/expenses" replace />} />
					<Route path="expenses/:id" element={<ExpenseEditRedirect />} />
					<Route path="expenses/:id/edit" element={<ExpenseEditRedirect />} />
					<Route
						path="payments"
						element={
							<PermissionRoute permission={["paymentMethods.read", "taxes.read", "settings.read"]}>
								<PaymentsHubPage />
							</PermissionRoute>
						}
					/>
					<Route path="taxes" element={<Navigate to="/payments?type=taxes" replace />} />
					<Route path="payment-methods" element={<Navigate to="/payments?type=methods" replace />} />
					<Route
						path="checkout"
						element={
							<PermissionRoute
								permission={[
									"cashierDiscount.read",
									"cashierGiftCard.read",
									"cashierStoreCredit.read",
									"cashierLoyalty.read",
								]}
							>
								<CheckoutHubPage />
							</PermissionRoute>
						}
					/>
					<Route path="checkout/coupons" element={<Navigate to="/checkout?type=coupons" replace />} />
					<Route path="checkout/gift-cards" element={<Navigate to="/checkout?type=giftCards" replace />} />
					<Route path="checkout/loyalty" element={<Navigate to="/checkout?type=loyalty" replace />} />
					<Route
						path="checkout/store-credit"
						element={<Navigate to="/checkout?type=storeCredit" replace />}
					/>
					<Route
						path="company"
						element={
							<PermissionRoute permission="company.read">
								<CompanyPage />
							</PermissionRoute>
						}
					/>
					<Route path="company/profile" element={<Navigate to="/company" replace />} />
					<Route path="subscription" element={<Navigate to="/company?goto=subscription" replace />} />
					<Route path="branches" element={<Navigate to="/company?goto=branches" replace />} />
					<Route path="categories" element={<Navigate to="/products" replace />} />
					<Route path="departments" element={<Navigate to="/management?type=departments" replace />} />
					<Route path="permissions" element={<Navigate to="/management?type=permissions" replace />} />
					<Route path="roles" element={<Navigate to="/management?type=roles" replace />} />
					<Route path="returns" element={<Navigate to="/sales?type=returns" replace />} />
					<Route
						path="returns/new"
						element={
							<PermissionRoute permission="returns.create">
								<AddReturnPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="returns/:id"
						element={
							<PermissionRoute permission="returns.read">
								<ReturnDetailPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="returns/:id/edit"
						element={
							<PermissionRoute permission="returns.update">
								<ReturnEditPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="stock-transfers/:id"
						element={
							<PermissionRoute permission="stockTransfers.read">
								<StockTransferDetailPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="notifications"
						element={
							<PermissionRoute permission="notifications.read">
								<NotificationsPage />
							</PermissionRoute>
						}
					/>
					<Route
						path="notifications/:id"
						element={
							<PermissionRoute permission="notifications.read">
								<NotificationDetailPage />
							</PermissionRoute>
						}
					/>
					<Route path="weight-sales-config" element={<Navigate to="/payments?type=scale" replace />} />
					<Route
						path="help"
						element={
							<PermissionRoute permission="dashboard.read">
								<HelpPage />
							</PermissionRoute>
						}
					/>
				</Route>
				<Route path="*" element={<Navigate to="/" replace />} />
			</Routes>

			<OfflineOverlay />
			<Toaster />
			<ConfirmDialogHost />
		</>
	);
}

const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }]);

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<ToastProvider>
				<ThemeProvider>
					<AuthProvider>
						<LanguageProvider>
							<RouterProvider router={router} />
						</LanguageProvider>
					</AuthProvider>
				</ThemeProvider>
			</ToastProvider>
		</QueryClientProvider>
	);
}

export default App;
